
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";
import type {
  CreateTestRequest,
  SubmitAnswerRequest,
} from "@/lib/types";

export function useTestHistory(examSlug: string, questionNumber?: number) {
  return useQuery({
    queryKey: queryKeys.tests.history(examSlug, questionNumber),
    queryFn: () => api.getTestHistory(examSlug, questionNumber),
    enabled: !!examSlug,
  });
}

export function useTestSession(examSlug: string, id: string) {
  return useQuery({
    queryKey: queryKeys.tests.session(examSlug, id),
    queryFn: () => api.getTestSession(examSlug, id),
    enabled: !!examSlug && !!id,
    staleTime: 0,
  });
}

export function useCreateTest(examSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTestRequest) => api.createTest(examSlug, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tests.all });
      qc.invalidateQueries({ queryKey: queryKeys.analytics.all });
    },
  });
}

export function useSubmitAnswer(examSlug: string, testId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SubmitAnswerRequest) =>
      api.submitAnswer(examSlug, testId, data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.tests.session(examSlug, testId),
      });
    },
  });
}

export function useCompleteTest(examSlug: string, testId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.completeTest(examSlug, testId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.tests.session(examSlug, testId),
      });
      qc.invalidateQueries({ queryKey: queryKeys.tests.history(examSlug) });
      qc.invalidateQueries({ queryKey: queryKeys.analytics.all });
    },
  });
}
